import React, { useEffect, useState, useCallback } from 'react';
import "./CreatorProfile.scss";
import { useDispatch, useSelector } from 'react-redux';
import { fetchUpdateProfilePic } from '../../Reducers/actions/userActions';
import { updateUser, getUserById } from '../../Reducers/actions/authActions';
import ProfilePicture from '../../components/ProfilePicture/ProfilePicture';
import ProfileCreations from '../../components/ProfileCreations/ProfileCreations';
import ProfileNews from '../../components/ProfileNews/ProfileNews';
import MenuProfile from '../../components/MenuProfile/MenuProfile';
import { fetchUserCreations, setActiveTab } from '../../Reducers/actions/creationsActions';
import { useNavigate } from 'react-router-dom';
import { fetchNewsByCreator } from '../../Reducers/actions/newsActions';
import { resetCreations } from '../../Reducers/actions/creationsActions';
import { USER_ROLES } from '../../constants/globals';
import AboutCreator from '../../components/AboutCreator/AboutCreator';
import BioPreview from '../../components/BioPreview/BioPreview';
import Icon from '../../components/Icon/Icon';
import ViewNetworksSheet from '../../components/ViewNetworksSheet/ViewNetworksSheet';
import { stopScrolling } from '../../Reducers/actions/navigationActions';
import Stats from '../../components/Stats/Stats';
import { usePullToRefresh } from '../../../hooks/usePullToRefresh';
import { cachingService } from '../../../helpers/cache';
import { getProfileShareUrl } from '../../../helpers/profilePaths';
import { getUserDisplayName } from '../../../helpers/userDisplayName';
import ModalMessage from '../../components/ModalMessage/ModalMessage';

const MENU_ITEMS = [
    {
        name: 'creation',
        label: 'Creations',
        render: (props) => <ProfileCreations {...props} />
    },
    {
        name: 'news',
        label: 'News',
        render: (props) => <ProfileNews {...props} />
    }
]

const CreatorProfile = () => {

    
    const {
        user,
    } = useSelector(state => ({
        user: state.auth?.user,
    }));
    const creationsLoading = useSelector(state => state.creation.loading);
    const newsLoading = useSelector(state => state.news.loading);
    
    const [selected, setSelected] = useState(MENU_ITEMS[0])
    const [showContributionInfos, setShowContributionInfos] = useState(false)
    const [showFullBio, setShowFullBio] = useState(false)
    // const [openMenu, setOpenMenu] = useState(false)
    const [showNetworks, setShowNetworks] = useState(false)
    const [showStats, setShowStats] = useState(false)
    const [copyFeedback, setCopyFeedback] = useState(null)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [firstLoad, setFirstLoad] = useState(false)

    /** URL others open to contribute to this creator (used in share-contribution modal). */
    const contributionUsername = user?.userName ? String(user.userName).replace(/^@/, '').trim() : ''
    const contributionUrl = contributionUsername ? `${window.location.origin}/contribute/${encodeURIComponent(contributionUsername)}` : ''

    const isLoading = creationsLoading || newsLoading;

    // Pull-to-refresh handler
    const handleRefresh = useCallback(async () => {
        if (!user?.user_id) return;
        
        try {
            // Clear cache to force fresh fetch
            cachingService.invalidate([`/api/profile/${user.user_id}?include_creations=true&include_news=true&include_subscribers=true&include_subscriptions=true`], true);
            cachingService.invalidate([`/api/creations/${user.user_id}?page=1&pageSize=20`], true);
            cachingService.invalidate([`/api/news/${user.user_id}`], true);
            
            // Fetch user info, creations and news in parallel
            const { success, data } = await getUserById(user.user_id);
            if (success && data) {
                dispatch({ type: 'SET_USER', payload: data });
                localStorage.setItem('pdp', data.profilePicture);
            }
            
            await Promise.all([
                dispatch(fetchUserCreations(user.user_id, 1, 20)),
                dispatch(fetchNewsByCreator(user.user_id))
            ]);
        } catch (error) {
            console.error('Error refreshing creator profile:', error);
        }
    }, [dispatch, user?.user_id]);

    // Pull-to-refresh hook
    const { containerRef, isRefreshing, pullDistance, pullProgress, shouldTrigger, shouldShowIndicator } = usePullToRefresh(
        handleRefresh,
        { 
            threshold: 80, 
            disabled: isLoading || !firstLoad || !user?.user_id 
        }
    );

    useEffect(() => {
        // dispatch(setLoadingCreation(true))

        if (user?.user_id && !firstLoad) {
            dispatch(fetchUserCreations(user?.user_id)).then(() => {
                setFirstLoad(true)
            })
            dispatch(fetchNewsByCreator(user?.user_id))
        }

        return () => {
            dispatch(setActiveTab('all'))
            dispatch(resetCreations());

        }
    }, [user?.user_id])


    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                const authorName = getUserDisplayName(user);
                const authorUsername = user?.userName
                    ? (user.userName.startsWith('@') ? user.userName : `@${user.userName}`)
                    : '';

                const shareTitle = `Check out ${authorUsername ? authorUsername : authorName}'s profile`;

                await navigator.share({
                    title: shareTitle,
                    url: getProfileShareUrl(user),
                });
            } catch (err) {
                console.error("Error sharing:", err);
            }
        } else {
            alert("Sharing is not supported on this device.");
        }
    }

    /** Share contribution link via native share (used inside contribution modal). */
    const handleShareContributionMore = useCallback(async () => {
        if (!contributionUrl) return
        const creatorName = user?.userName ? (user.userName.startsWith('@') ? user.userName : `@${user.userName}`) : user?.firstName || 'Me'
        const shareTitle = `Support ${creatorName} on HYK`
        if (navigator.share) {
            try {
                await navigator.share({ title: shareTitle, url: contributionUrl })
            } catch (err) {
                if (err?.name !== 'AbortError') {
                    try {
                        await navigator.clipboard.writeText(contributionUrl)
                        setCopyFeedback('Link copied!')
                        setTimeout(() => setCopyFeedback(null), 2000)
                    } catch {
                        alert('Sharing not supported. Copy the link from "Share link".')
                    }
                }
            }
        } else {
            try {
                await navigator.clipboard.writeText(contributionUrl)
                setCopyFeedback('Link copied!')
                setTimeout(() => setCopyFeedback(null), 2000)
            } catch {
                alert('Link: ' + contributionUrl)
            }
        }
    }, [contributionUrl, user?.userName, user?.firstName])

    /** Copy contribution link to clipboard and show feedback. */
    const handleCopyContributionLink = useCallback(async (message = 'Link copied!') => {
        if (!contributionUrl) return
        try {
            await navigator.clipboard.writeText(contributionUrl)
            setCopyFeedback(message)
            setTimeout(() => setCopyFeedback(null), 2000)
        } catch {
            try {
                document.execCommand('copy', false, contributionUrl)
                setCopyFeedback(message)
                setTimeout(() => setCopyFeedback(null), 2000)
            } catch {
                alert(contributionUrl)
            }
        }
    }, [contributionUrl])

    /** Open X (Twitter) share intent with contribution link. */
    const handleShareContributionViaX = useCallback(() => {
        if (!contributionUrl) return
        const text = encodeURIComponent('Support me on HYK ')
        const url = encodeURIComponent(contributionUrl)
        window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'noopener,noreferrer')
    }, [contributionUrl])

    /** Open Facebook share dialog; also copy link and remind user to add text (Facebook doesn't allow pre-filled text in share URL). */
    const handleShareContributionViaFacebook = useCallback(() => {
        if (!contributionUrl) return
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(contributionUrl)}`, '_blank', 'noopener,noreferrer')
        try {
            navigator.clipboard.writeText(contributionUrl).then(() => {
                setCopyFeedback('Link copied. Add your message in the post on Facebook.')
                setTimeout(() => setCopyFeedback(null), 3000)
            })
        } catch {
            setCopyFeedback('Add your message in the post on Facebook.')
            setTimeout(() => setCopyFeedback(null), 3000)
        }
    }, [contributionUrl])


    const onImageUpload = (obj) => {
        fetchUpdateProfilePic(obj, user.user_id).then(({ success, data }) => {
            if (success) {
                dispatch(updateUser({ profilePicture: data }))
                localStorage.setItem("pdp", data)
            }
        })
    }

    const pdp = localStorage.getItem("pdp")

    const handleShowStats = (value) => {
        if (user) {
            setShowStats(value)
            dispatch(stopScrolling(!!value))
        }
    }

    return (
        <div className={`creator-profile pt-40 mt-32 ${showStats ? 'stats-shown' : ''}`} ref={containerRef}>
            {/* Pull-to-refresh indicator */}
            {(shouldShowIndicator || isRefreshing || shouldTrigger) && (
                <div 
                    className="pull-to-refresh-indicator"
                    style={{
                        transform: `translateY(${Math.min(pullDistance, 1)}px)`,
                        opacity: Math.min(pullProgress, 1)
                    }}
                >
                    <div className="pull-to-refresh-spinner">
                        {isRefreshing ? (
                            <div className="spinner" />
                        ) : (
                            <Icon 
                                alias="chevron-down" 
                                width={24} 
                                color="#FF3232"
                                style={{
                                    transform: `rotate(${shouldTrigger ? 180 : 0}deg)`,
                                    transition: 'transform 0.2s ease'
                                }}
                            />
                        )}
                    </div>
                    <p className="pull-to-refresh-text">
                        {isRefreshing ? 'Refreshing...' : shouldTrigger ? 'Release to refresh' : 'Pull to refresh'}
                    </p>
                </div>
            )}
            
            {showFullBio && <AboutCreator user={user} onClose={() => setShowFullBio(false)} />}
            {showNetworks && <ViewNetworksSheet
                instagram={user?.instagram}
                facebook={user?.facebook}
                twitter={user?.twitter}
                youtube={user?.youtube}
                onClose={() => setShowNetworks(false)}
            />}
            {showStats && <Stats user={user} onClose={() => { handleShowStats(null) }} index={showStats} fromProfile={true} />}
            {/* <div className='d-flex aic px-16 py-24'>
                <Icon alias={"chevron-left"} stroke={"#0A0F1E"} color="#0A0F1E" width={24} onClick={() => navigate(-1)} />
                <p className='fs-sm2 fg-dark fw-bold tt-uppercase ml-12'>{(user?.userName || getUserDisplayName(user)).toUpperCase()}</p>
                <span className="actions ml-auto d-flex aic">
                    <span className="icon-container" onClick={() => setShowNetworks(true)}>
                        <Icon alias={"navigation"} color="#FF3232" width={16} height={16} />
                    </span>
                    <span className="icon-container ml-16 c-pointer" onClick={handleNativeShare}>
                        <Icon alias={"share"} stroke="#FF3232" color='none' width={20} height={20} />
                    </span>
                    <span className="icon-container d-flex p-relative ml-16">
                        <Icon alias={"three_dots"} strokeWidth={2} stroke="#FF3232" width={24} onClick={(e) => { e.stopPropagation(); setOpenMenu(!openMenu) }} />
                        {openMenu && (
                            <div className="news-menu-dropdown" onClick={() => setOpenMenu(false)}>
                                <div className="news-menu-content">


                                </div>
                            </div>
                        )}
                    </span>
                </span>
            </div> */}
            <div className="creator-profile__sidebar">
            <div className="d-flex aic mx-16 mb-16">
                <div style={{ minWidth: '72px', minHeight: '72px' }}>
                    <ProfilePicture
                        imgUrl={!pdp || pdp === 'null' || pdp.length === 0 ? user?.profilePicture : pdp}
                        onImageUpload={onImageUpload}
                        width={72}
                        height={72}
                    />
                </div>
                <div className="ml-16">
                    <p className='fg-dark fw-bold fs-regular tt-uppercase mt-0 mb-8'>{getUserDisplayName(user).toUpperCase()}</p>
                    <div className="d-flex aic">
                        <div className="d-flex f-column fg-dark" onClick={() => handleShowStats(1)}>
                            <p className="fw-bold">{user?.subscribers_number || 0}</p>
                            <p className="fs-tiny fw-medium tt-uppercase">Subscribers</p>
                        </div>
                        <div className="d-flex f-column fg-dark ml-16" onClick={() => handleShowStats(2)}>
                            <p className="fw-bold">{user?.subscriptions_number || 0}</p>
                            <p className="fs-tiny fw-medium tt-uppercase">Subscriptions</p>
                        </div>
                        <div className="d-flex f-column fg-dark ml-16" onClick={() => handleShowStats(4)}>
                            <p className="fw-bold">{user?.nbContributions || 0}</p>
                            <p className="fs-tiny fw-medium tt-uppercase">Contributions</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mx-16 mb-16 bio">
                <div className='my-0 fs-real fw-medium fg-black bio-text-wrap'>
                    <BioPreview text={user?.bio} onMore={() => setShowFullBio(true)} />
                </div>
            </div>
            <div className='d-flex mx-16 gap-8 mb-8'>
                <button className='btn red-outline-btn btn-sm fs-tiny fw-extra-bold rounded-xl btn-full' onClick={() => navigate('/edit-profile')}>Edit profile</button>
                <button className='btn red-outline-btn btn-sm fs-tiny fw-extra-bold rounded-xl btn-full' onClick={() => navigate('/edit-networks')}>Edit networks</button>
                <button className='btn red-btn btn-sm rounded-xl d-flex aic jcc share-profile-btn' onClick={handleNativeShare}>
                    <Icon alias={"share"} stroke="#FFF" color='none' width={20} height={20} />
                </button>
            </div>

            <div className='contribute-profile d-flex f-column jcc mt-24 py-16 px-16 mx-16 mb-24'>
                <div className='d-flex f-row aic jcsb'>
                {!user?.nbContributors ?
                    <span className='fg-dark fw-medium mx-auto'>Your contributors will show here</span>
                    : <span className='fg-dark fw-medium' onClick={() => handleShowStats(3)}><span className='fw-bold'>{user.nbContributors || 0}</span>{" "}Contributors</span>
                }

                {typeof user?.nbContributors === 'number' && user?.nbContributors > 0 &&
                    <button onClick={() => navigate('/earnings')} className="btn-sm red-outline-btn ml-auto fs-tiny fw-extra-bold">
                        Earnings
                    </button>}
                    </div>
                    <div className='contribution-link d-flex aic jcc mt-16 px-16 py-8 ' onClick={() => setShowContributionInfos(true)}>
                <Icon alias="share-contribution-link" stroke="#FFF" color='#FFF' width={20} height={20} />
                <span className='share-contribution-link-text ml-4'>Share contribution link</span>
            </div>
            </div>
            </div>

            <div className="creator-profile__content">
            {user?.role !== USER_ROLES.customer && (
                <MenuProfile
                    menu_items={MENU_ITEMS}
                    selected={selected}
                    setSelected={setSelected}
                    className='my-profile-menu'
                    user={user}
                    firstLoad={firstLoad}
                />
            )}
            </div>
            {showContributionInfos &&
            <ModalMessage withoutMaxHeight={true} onClose={() => { setShowContributionInfos(false); setCopyFeedback(null); }} style={{paddingBottom: '48px'}}>

                <p className='ta-center fs-real fw-bold'>Share your contribution link</p>
                <div className='info-contribution-div d-flex f-column aic mt-16'>
                    <ProfilePicture
                        imgUrl={user?.profilePicture}
                        width={80}
                        height={80}
                        no_edit={true}
                    />
                    <p className='fs-sm2 fw-bold mt-16'>{user?.userName}</p>
                    <div className="vertical-dashed-line" aria-hidden />
                    <Icon alias="contribute" stroke="#FF3232" color='#FF3232' width={80} height={80} />
                    <div className='contribute-button-link mt-24 p-24 d-flex f-column aic jcc'>
                        <p className='fs-sm2 fw-extra-bold fg-white '>Contribute on
                        <Icon alias="hyk-logo-wordmark" width={70} height={23} color="white" className={"ml-8"}/>   </p>    
                        <Icon alias="hyk-logo-icon" width={42} height={42} color="white" className={"mt-16"} />
                                     </div>
                </div>

                <div className='share-options-row d-flex gap-16 mx-16 mt-24 mb-16'>
                    <div
                        className='icon-container d-flex f-column aic'
                        role="button"
                        tabIndex={0}
                        onClick={handleShareContributionMore}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleShareContributionMore(); } }}
                        aria-label="More share options"
                    >
                        <span className='share-option-circle d-flex aic jcc'>
                            <Icon alias="more-link" width={28} height={28} />
                        </span>
                        <span className='share-option-label mt-8'>More</span>
                    </div>
                    <div
                        className='icon-container d-flex f-column aic'
                        role="button"
                        tabIndex={0}
                        onClick={() => handleCopyContributionLink('Link copied!')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCopyContributionLink('Link copied!'); } }}
                        aria-label="Copy contribution link"
                    >
                        <span className='share-option-circle d-flex aic jcc'>
                            <Icon alias="share-link" width={28} height={28} />
                        </span>
                        <span className='share-option-label mt-8'>Share link</span>
                    </div>
                    <div
                        className='icon-container d-flex f-column aic'
                        role="button"
                        tabIndex={0}
                        aria-label="Share on X (Twitter)"
                        onClick={handleShareContributionViaX}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleShareContributionViaX(); } }}
                    >
                        <span className='share-option-circle d-flex aic jcc'>
                            <Icon alias="twitter-icon" width={32} height={32} color="#FF3232" />
                        </span>
                        <span className='share-option-label mt-8'>X</span>
                    </div>
                    <div
                        className='icon-container d-flex f-column aic'
                        role="button"
                        tabIndex={0}
                        aria-label="Share on Facebook"
                        onClick={handleShareContributionViaFacebook}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleShareContributionViaFacebook(); } }}
                    >
                        <span className='share-option-circle d-flex aic jcc'>
                            <Icon alias="facebook-icon" width={32} height={32} />
                        </span>
                        <span className='share-option-label mt-8'>Facebook</span>
                    </div>
                </div>
                {copyFeedback && (
                    <p className="share-copy-feedback ta-center fw-medium mt-8 mb-16" role="status">
                        {copyFeedback}
                    </p>
                )}
            </ModalMessage>}

        </div>
    );
};

export default CreatorProfile;